import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Container, Row, Col, Card, Button, Modal, Toast, Badge, Spinner } from 'react-bootstrap';
import { FaTrash, FaPlus, FaEdit, FaCalendarAlt, FaTag, FaRedo } from 'react-icons/fa';
import { getProjects, deleteProject, getProjectByGuid } from '../../services/projectService';
import ProjectForm from './ProjectForm';

const CATEGORY_OPTIONS = [
  { key: 'genel', label: 'Genel' },
  { key: 'iskoop', label: 'İSKOOP' }
];

const ProjectList = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeCategory, setActiveCategory] = useState('genel');

  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);

  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editModalLoading, setEditModalLoading] = useState(false);
  const [editingProject, setEditingProject] = useState(null);

  const [addModalVisible, setAddModalVisible] = useState(false);

  const [toastState, setToastState] = useState({
    show: false,
    message: '',
    variant: 'success'
  });

  const showToast = (message, variant = 'success') => {
    setToastState({
      show: true,
      message,
      variant
    });
  };

  const hideToast = () => {
    setToastState((prev) => ({
      ...prev,
      show: false
    }));
  };

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const response = await getProjects(activeCategory);

      if (Array.isArray(response)) {
        setProjects(response);
        return;
      }

      if (response?.isSuccess && Array.isArray(response.data)) {
        setProjects(response.data);
        return;
      }

      throw new Error(response?.resultMessage ?? 'Beklenmeyen bir sonuç alındı.');
    } catch (err) {
      console.error('Error while fetching projects:', err);
      setProjects([]);
      setError('Projeler yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  }, [activeCategory]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const confirmDelete = (project) => {
    setSelectedProject(project);
    setDeleteModalVisible(true);
  };

  const handleDelete = async () => {
    if (!selectedProject) return;

    try {
      const response = await deleteProject(selectedProject.guid);
      if (response?.isSuccess) {
        setProjects((prev) => prev.filter((item) => item.guid !== selectedProject.guid));
        showToast('Proje başarıyla silindi.');
      } else {
        showToast(response?.resultMessage ?? 'Proje silinirken bir hata oluştu.', 'danger');
      }
    } catch (err) {
      console.error('Delete project error:', err);
      showToast('Proje silinirken bir hata oluştu.', 'danger');
    } finally {
      setDeleteModalVisible(false);
      setSelectedProject(null);
    }
  };

  const handleEdit = async (project) => {
    setEditModalVisible(true);
    setEditModalLoading(true);
    setEditingProject(null);
    try {
      const detail = await getProjectByGuid(project.guid);
      const normalized = detail?.data ?? detail;
      if (!normalized) {
        throw new Error('Proje detayları getirilemedi.');
      }
      setEditingProject({
        ...normalized,
        guid: normalized.guid ?? project.guid
      });
    } catch (err) {
      console.error('Error while loading project details:', err);
      setEditModalVisible(false);
      showToast('Proje detayları yüklenirken bir hata oluştu.', 'danger');
    } finally {
      setEditModalLoading(false);
    }
  };

  const handleAdd = () => {
    setAddModalVisible(true);
  };

  const handleEditSuccess = async () => {
    await fetchProjects();
    setEditModalVisible(false);
    setEditingProject(null);
    showToast('Proje başarıyla güncellendi.');
  };

  const handleAddSuccess = async () => {
    await fetchProjects();
    setAddModalVisible(false);
    showToast('Proje başarıyla eklendi.');
  };

  const getStatusBadgeVariant = (status) => {
    if (!status) return 'secondary';
    const statusLower = status.toLowerCase();

    if (statusLower.includes('devam') || statusLower.includes('sürüyor')) {
      return 'warning';
    }

    if (
      statusLower.includes('tamam') ||
      statusLower.includes('açıldı') ||
      statusLower.includes('bitti')
    ) {
      return 'success';
    }

    if (statusLower.includes('iptal')) {
      return 'danger';
    }

    return 'secondary';
  };

  const categoryButtons = useMemo(
    () =>
      CATEGORY_OPTIONS.map((category) => (
        <Button
          key={category.key}
          variant={category.key === activeCategory ? 'primary' : 'outline-primary'}
          onClick={() => setActiveCategory(category.key)}
        >
          {category.label}
        </Button>
      )),
    [activeCategory]
  );

  return (
    <Container fluid className="py-4">
      <Card className="mb-4">
        <Card.Body className="d-flex flex-column flex-md-row align-items-md-center justify-content-between">
          <div>
            <h3 className="mb-2 mb-md-0">Projeler</h3>
            <div className="d-flex flex-wrap gap-2">{categoryButtons}</div>
          </div>
          <div className="d-flex gap-2 mt-3 mt-md-0">
            <Button variant="outline-secondary" onClick={fetchProjects} disabled={loading}>
              <FaRedo className="me-2" />
              Yenile
            </Button>
            <Button variant="primary" onClick={handleAdd}>
              <FaPlus className="me-2" />
              Yeni Proje Ekle
            </Button>
          </div>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          {loading ? (
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Yükleniyor...</span>
              </Spinner>
            </div>
          ) : error ? (
            <div className="text-center py-5 text-danger fw-semibold">{error}</div>
          ) : projects.length === 0 ? (
            <div className="text-center py-5 text-muted">Bu kategoride proje bulunmuyor.</div>
          ) : (
            <Row className="g-4">
              {projects.map((project) => (
                <Col key={project.guid} xs={12} md={6} lg={4}>
                  <Card className="h-100 shadow-sm">
                    {project.image && (
                      <Card.Img
                        variant="top"
                        src={project.image}
                        alt={project.title}
                        style={{ height: 200, objectFit: 'cover' }}
                      />
                    )}
                    <Card.Body>
                      <Card.Title className="mb-3">{project.title}</Card.Title>
                      <div className="mb-3 d-flex flex-wrap gap-2">
                        <Badge bg={getStatusBadgeVariant(project.status)}>
                          {project.status ?? 'Durum Belirtilmedi'}
                        </Badge>
                        <Badge bg="secondary">
                          <FaTag className="me-1" />
                          {project.category ?? 'Kategori'}
                        </Badge>
                      </div>
                      {project.summary && (
                        <Card.Text className="text-muted">
                          {project.summary.length > 120
                            ? `${project.summary.substring(0, 120)}…`
                            : project.summary}
                        </Card.Text>
                      )}
                      {project.projectDate && (
                        <small className="text-muted d-flex align-items-center gap-1">
                          <FaCalendarAlt />{' '}
                          {new Date(project.projectDate).toLocaleDateString('tr-TR')}
                        </small>
                      )}
                    </Card.Body>
                    <Card.Footer className="bg-white">
                      <div className="d-flex gap-2">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleEdit(project)}
                        >
                          <FaEdit className="me-1" />
                          Düzenle
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => confirmDelete(project)}
                        >
                          <FaTrash className="me-1" />
                          Sil
                        </Button>
                      </div>
                    </Card.Footer>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </Card.Body>
      </Card>

      <Modal show={deleteModalVisible} onHide={() => setDeleteModalVisible(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Proje Silme</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Bu projeyi silmek istediğinizden emin misiniz?
          <br />
          <strong>{selectedProject?.title}</strong>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteModalVisible(false)}>
            İptal
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Sil
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={editModalVisible}
        onHide={() => {
          setEditModalVisible(false);
          setEditingProject(null);
        }}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Proje Düzenle</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editModalLoading ? (
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Yükleniyor...</span>
              </Spinner>
            </div>
          ) : (
            <ProjectForm
              editingProject={editingProject}
              onSubmit={handleEditSuccess}
              onCancel={() => setEditModalVisible(false)}
              isModal
            />
          )}
        </Modal.Body>
      </Modal>

      <Modal
        show={addModalVisible}
        onHide={() => setAddModalVisible(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Yeni Proje Ekle</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ProjectForm
            editingProject={null}
            onSubmit={handleAddSuccess}
            onCancel={() => setAddModalVisible(false)}
            isModal
            initialCategory={activeCategory}
          />
        </Modal.Body>
      </Modal>

      <Toast
        show={toastState.show}
        onClose={hideToast}
        delay={3000}
        autohide
        bg={toastState.variant}
        style={{
          position: 'fixed',
          top: 20,
          right: 20,
          zIndex: 1055
        }}
      >
        <Toast.Body className="text-white">{toastState.message}</Toast.Body>
      </Toast>
    </Container>
  );
};

export default ProjectList;
